import { FC, useMemo } from "react";
import { MainLayoutContainer } from "components/Layout";
import { StepsLayoutContainerControlled } from "components/Layout/StepsLayoutContainerControlled";
import { REQUIREMENT } from "helpers/constants";
import { STEPS, STEP_LABELS } from "./config";
import { ImportMode, RequirementImportType } from "./types";
import { useImportRequirements } from "./useImportRequirements";
import { UploadStep } from "./steps/UploadStep";
import { ProcessingStep } from "./steps/ProcessingStep";
import { ReviewStep } from "./steps/ReviewStep";
import { CompleteStep } from "./steps/CompleteStep";

type Props = {
  mode: ImportMode;
  requirementType: RequirementImportType;
};

export const ImportRequirements: FC<Props> = ({ mode, requirementType }) => {
  const {
    step,
    activeLabel,
    requirements,
    processing,
    uploadError,
    loading,
    loadingProgress,
    report,
    organizationName,
    speciesList,
    handleStep,
    setProcessing,
    handleRequirementsFile,
    handleDownloadRequirementTemplate,
    onSubmit,
    handleFinish,
    handleCancel,
    resolveCompetencyLinks,
  } = useImportRequirements({ mode, requirementType });

  const typeLabel = useMemo(() => {
    if (requirementType === REQUIREMENT.REQUIREMENT_EPA) return "EPAs";
    if (requirementType === REQUIREMENT.REQUIREMENT_ITER) return "ITERs";
    return "Competencies";
  }, [requirementType]);

  const title = useMemo(() => {
    const scope =
      mode === "org"
        ? `Import ${typeLabel}${organizationName ? ` — ${organizationName}` : ""}`
        : `Import ${typeLabel} (Global Catalog)`;
    return scope;
  }, [mode, organizationName, typeLabel]);

  const stepComponentList = [
    {
      label: STEPS.UPLOAD,
      component: (
        <UploadStep
          requirementType={requirementType}
          requirements={requirements}
          resolveCompetencyLinks={resolveCompetencyLinks}
          error={uploadError}
          onDownloadTemplate={handleDownloadRequirementTemplate}
          onFileUploaded={handleRequirementsFile}
          onNext={() => handleStep(STEPS.PROCESSING)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      label: STEPS.PROCESSING,
      component: (
        <ProcessingStep
          requirementType={requirementType}
          requirements={requirements}
          resolveCompetencyLinks={resolveCompetencyLinks}
          processing={processing}
          species={speciesList}
          onProcessingChange={setProcessing}
          onBack={() => handleStep(STEPS.UPLOAD)}
          onNext={() => handleStep(STEPS.REVIEW)}
        />
      ),
    },
    {
      label: STEPS.REVIEW,
      component: (
        <ReviewStep
          requirementType={requirementType}
          requirements={requirements}
          resolveCompetencyLinks={resolveCompetencyLinks}
          processing={processing}
          loading={loading}
          loadingProgress={loadingProgress}
          onBack={() => handleStep(STEPS.PROCESSING)}
          onSubmit={onSubmit}
        />
      ),
    },
    {
      label: STEPS.COMPLETE,
      component: <CompleteStep report={report} onFinish={handleFinish} />,
    },
  ];

  return (
    <MainLayoutContainer title={title} maxWidth={1200}>
      <StepsLayoutContainerControlled
        steps={STEP_LABELS}
        StepComponentList={stepComponentList}
        activeStep={step}
        activeLabel={activeLabel}
      />
    </MainLayoutContainer>
  );
};

export const UploadCSVRequirements = ImportRequirements;
